import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { createProject as createProjectAction } from '../controllers/projectsController';
import { searchMembersWithSubMembers } from '../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../api/httpClientGroups';

const statusOptions = [
  { value: 0, label: 'Planlandı' },
  { value: 1, label: 'Devam Ediyor' },
  { value: 2, label: 'Tamamlandı' },
  { value: 3, label: 'Beklemede' },
  { value: 4, label: 'İptal' },
];

const CreateProjectDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [theme, setTheme] = useState('');
  const [purpose, setPurpose] = useState('');
  const [status, setStatus] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [logoCariCode, setLogoCariCode] = useState('');

  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [leaderId, setLeaderId] = useState('');

  const [coverFile, setCoverFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!open) return;
    if (!memberQuery || memberQuery.length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembersWithSubMembers(memberQuery)
      .then((data) => {
        if (!active) return;
        const flattened = [];
        (Array.isArray(data) ? data : []).forEach((item) => {
          if (!item) return;
          // Eğer direkt olarak type property'si varsa (flat array), onu kullan
          if (typeof item.type === 'number') {
            flattened.push({
              id: item.id,
              fullName: item.fullName,
              profilePictureUrl: item.profilePictureUrl,
              type: item.type === 1 ? 'SubMember' : 'Member',
              parentMemberId: item.parentMemberId || null,
            });
          } else {
            // Eğer nested yapı varsa (member içinde subMembers), eski mantık
            flattened.push({
              id: item.id,
              fullName: item.fullName,
              profilePictureUrl: item.profilePictureUrl,
              type: 'Member',
            });
            if (Array.isArray(item.subMembers)) {
              item.subMembers.forEach((s) => {
                if (!s) return;
                flattened.push({
                  id: s.id,
                  fullName: s.fullName,
                  profilePictureUrl: s.profilePictureUrl,
                  type: 'SubMember',
                  parentMemberId: item.id,
                  parentFullName: item.fullName,
                });
              });
            }
          }
        });
        setMemberOptions(flattened);
      })
      .catch(() => {
        if (!active) return;
        setMemberOptions([]);
      })
      .finally(() => {
        if (active) setMemberLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, memberQuery]);

  const canSubmit = useMemo(
    () =>
      name.trim().length > 0 &&
      logoCariCode.trim().length > 0 &&
      selectedMembers.length > 0 &&
      leaderId,
    [name, logoCariCode, selectedMembers.length, leaderId]
  );

  const reset = () => {
    setName('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setTheme('');
    setPurpose('');
    setStatus(0);
    setIsActive(true);
    setLogoCariCode('');
    setMemberQuery('');
    setMemberOptions([]);
    setSelectedMembers([]);
    setLeaderId('');
    setCoverFile(null);
    setCoverImagePreview(null);
    setFileInputKey(prev => prev + 1);
    setError('');
    setSuccess('');
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        groupMembers: selectedMembers.map((m) => ({
          id: m.id,
          fullName: m.fullName,
          profilePictureUrl: m.profilePictureUrl || null,
          role: null,
          isLeader: m.id === leaderId,
          // ProjectMemberType: 0 = Member, 1 = SubMember
          memberType: m.type === 'SubMember' ? 1 : 0,
          parentMemberId: m.type === 'SubMember' ? m.parentMemberId || null : null,
        })),
        startDate: startDate || null,
        endDate: endDate || null,
        theme: theme.trim() || null,
        purpose: purpose.trim() || null,
        logoCariCode: logoCariCode.trim(),
        status,
        isActive,
      };

      const created = await createProjectAction(payload, coverFile);
      setSuccess('Proje oluşturuldu');
      onCreated?.(created);
      setTimeout(handleClose, 600);
    } catch (err) {
      setError(err?.message || 'Proje oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Yeni Proje
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Proje Adı
              </Typography>
          <TextField
                fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Açıklama
              </Typography>
          <TextField
                fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
                placeholder=""
            multiline
            minRows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
          />
            </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Başlangıç Tarihi
                </Typography>
            <TextField
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Bitiş Tarihi
                </Typography>
            <TextField
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
            />
              </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Tema
                </Typography>
            <TextField
                  fullWidth
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Amaç
                </Typography>
            <TextField
                  fullWidth
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>
          </Stack>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Logo Cari Kod
            </Typography>
            <TextField
              fullWidth
              required
              value={logoCariCode}
              onChange={(e) => setLogoCariCode(e.target.value)}
              placeholder=""
              disabled={submitting}
              inputProps={{ maxLength: 60 }}
              helperText="Proje bağışı entegrasyonu için Logo ERP cari kodu (zorunlu)."
              FormHelperTextProps={{ sx: { mt: 0.75, fontSize: '12px' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '5px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0E4D94',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.2,
                  px: 1.5,
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e6e6e6',
                },
              }}
            />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Durum
                </Typography>
            <TextField
              select
                  fullWidth
              value={status}
              onChange={(e) => setStatus(Number(e.target.value))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
            >
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Durum
                  </Typography>
            <FormControlLabel
              control={
                      <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#0E4D94',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#0E4D94',
                          },
                        }}
                />
              }
                    label={isActive ? 'Aktif' : 'Pasif'}
                    sx={{
                      ml: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '13px',
                        ml: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
          </Stack>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Proje Ekibi
            </Typography>
            <Autocomplete
              multiple
              options={memberOptions}
              loading={memberLoading}
              value={selectedMembers}
              onChange={(e, v) => {
                setSelectedMembers(v || []);
                if (v && v.length > 0 && !v.some((m) => m.id === leaderId)) {
                  setLeaderId(v[0].id);
                }
                if (!v || v.length === 0) {
                  setLeaderId('');
                }
              }}
              onInputChange={(e, v) => setMemberQuery(v)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o?.fullName || ''}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Avatar
                    src={resolveGroupsAssetUrl(option.profilePictureUrl)}
                    sx={{ width: 28, height: 28 }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">
                      {option.fullName}
                    </Typography>
                    {option.type === 'SubMember' && option.parentFullName && (
                      <Typography variant="caption" color="text.secondary">
                        Üye: {option.parentFullName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.fullName}
                    avatar={<Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 20, height: 20 }} />}
                    size="small"
                    sx={{
                      backgroundColor: '#E7EDF4',
                      color: '#0E4D94',
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-deleteIcon': {
                        fontSize: '14px',
                        color: '#0E4D94',
                        '&:hover': {
                          color: '#0a3d7a',
                        },
                      },
                    }}
                  />
                ))
              }
              slotProps={{ paper: { sx: { width: 560 } }, listbox: { sx: { maxHeight: 360 } } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedMembers.length > 0 ? `${selectedMembers.length} üye seçildi` : 'Üye ara ve ekle...'}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      backgroundColor: '#fafafa',
                      minHeight: '36px',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                          borderWidth: '1px',
                        },
                      },
                    },
                    '& .MuiAutocomplete-inputRoot': {
                      py: selectedMembers.length > 0 ? '2px' : '4px',
                      px: '10px',
                    },
                    '& .MuiAutocomplete-input': {
                      py: 0,
                      px: 0,
                      fontSize: '13px',
                    },
                    '& .MuiInputBase-input': {
                      py: 0.8,
                      px: 1,
                      fontSize: '13px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                      borderWidth: '1px',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {memberLoading ? <CircularProgress color="inherit" size={14} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedMembers.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Proje Lideri
                </Typography>
              <TextField
                select
                  fullWidth
                value={leaderId}
                onChange={(e) => setLeaderId(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
              >
                {selectedMembers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.fullName}
                  </MenuItem>
                ))}
              </TextField>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Kapak Görseli (opsiyonel)
            </Typography>
              <input
              key={`cover-image-${fileInputKey}`}
              accept="image/*"
              style={{ display: 'none' }}
              id="cover-image-upload"
                type="file"
                onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  setCoverFile(selectedFile);
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setCoverImagePreview(reader.result);
                  };
                  reader.readAsDataURL(selectedFile);
                }
                }}
              />
            {!coverFile ? (
              <label htmlFor="cover-image-upload">
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 2,
                    px: 2,
                    border: '2px dashed #e6e6e6',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    backgroundColor: '#fafafa',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#0E4D94',
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <ImageIcon sx={{ fontSize: '20px', color: '#0E4D94' }} />
                  <Typography variant="body2" sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500 }}>
                    Kapak Görseli Seç
                  </Typography>
                </Box>
              </label>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  {coverImagePreview && (
                    <Box
                      component="img"
                      src={coverImagePreview}
                      alt="Kapak görseli önizleme"
                      sx={{
                        width: 120,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: '5px',
                        border: '1px solid #e6e6e6',
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5 }}>
                {coverFile.name}
              </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                      {(coverFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCoverFile(null);
                        setCoverImagePreview(null);
                      }}
                      sx={{
                        backgroundColor: '#FFEEF3',
                        borderRadius: '6px',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          backgroundColor: '#FFD6E0',
                        },
                        '& svg': {
                          fontSize: '16px',
                          color: '#F8285A',
                        },
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ py: 2, px: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: '#666666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          İptal
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !canSubmit}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            backgroundColor: '#0E4D94',
            borderRadius: '5px',
            px: 2,
            '&:hover': {
              backgroundColor: '#0a3d7a',
            },
            '&:disabled': {
              backgroundColor: '#e6e6e6',
              color: '#a3a3a3',
            },
          }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateProjectDialog;

